import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { uploadApplicationDocument } from "@/services/storageService";
import { Application, useApplication } from "@/contexts/ApplicationContext";
import { Dispatch, SetStateAction } from "react";
import { useDocumentCloudFunctions } from "@/hooks/useDocumentCloudFunctions";
import * as Sentry from "@sentry/react";

interface UseDocumentSubmissionHandlersProps {
  // DVLA props
  drivingLicenceNumber: string;
  licenseFrontFile: File | null;
  licenseFrontPreview: string | null;
  licenseBackFile: File | null;
  licenseBackPreview: string | null;
  dvlaAttempts: number;
  setDvlaAttempts: Dispatch<SetStateAction<number>>;

  // RTW props
  rtwShareCode: string;
  documentFile: File | null;
  documentPreview: string | null;
  rtwAttempts: number;
  setRtwAttempts: Dispatch<SetStateAction<number>>;

  // Insurance props
  insuranceProvider: string;
  policyNumber: string;
  insuranceProofFile: File | null;
  insuranceProofPreview: string | null;

  // Additional docs
  cpcFrontFile: File | null;
  cpcBackFile: File | null;
  cpcFrontPreview: string | null;
  cpcBackPreview: string | null;
  tachoFrontFile: File | null;
  tachoBackFile: File | null;
  tachoFrontPreview: string | null;
  tachoBackPreview: string | null;
  tachoCardNumber: string;
  d4MedicalFile: File | null;
  d4MedicalPreview: string | null;

  // Application state management
  setApplication: Dispatch<SetStateAction<Application>>;
  setSubmittedTabs: Dispatch<SetStateAction<string[]>>;
  setActiveTab: (tab: string) => void;
}

export const useDocumentSubmissionHandlers = ({
  drivingLicenceNumber,
  licenseFrontFile,
  licenseFrontPreview,
  licenseBackFile,
  licenseBackPreview,
  rtwShareCode,
  documentFile,
  documentPreview,
  insuranceProvider,
  policyNumber,
  insuranceProofFile,
  cpcFrontFile,
  cpcBackFile,
  cpcFrontPreview,
  cpcBackPreview,
  tachoFrontFile,
  tachoBackFile,
  tachoFrontPreview,
  tachoBackPreview,
  tachoCardNumber,
  d4MedicalFile,
  d4MedicalPreview,
  dvlaAttempts,
  setDvlaAttempts,
  rtwAttempts,
  setRtwAttempts,
  setSubmittedTabs,
  setActiveTab,
}: UseDocumentSubmissionHandlersProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { application, refetch } = useApplication();
  const {
    submitDVLA,
    submitRTW,
    submitInsurance,
    submitCPC,
    submitTachoCard,
    submitD4Medical,
  } = useDocumentCloudFunctions();

  // Centralized navigation/tab progression handler
  const handleNextNavigation = async (
    result: unknown,
    fallback: () => void
  ) => {
    await refetch();
    // Helper to safely extract string props
    const tryGet = (
      obj: unknown,
      key: "nextStage" | "nextSegment"
    ): string | undefined => {
      if (obj && typeof obj === "object") {
        const val = (obj as Record<string, unknown>)[key];
        return typeof val === "string" ? val : undefined;
      }
      return undefined;
    };

    // Support both shapes: top-level and nested under `data`
    let nested: unknown;
    if (result && typeof result === "object" && "data" in result) {
      nested = (result as { data: unknown }).data;
    }

    const nextStage =
      tryGet(result, "nextStage") ?? tryGet(nested, "nextStage");
    if (nextStage) {
      navigate(`/${nextStage}`);
      return;
    }

    const nextSegment =
      tryGet(result, "nextSegment") ?? tryGet(nested, "nextSegment");
    if (nextSegment) {
      setActiveTab(nextSegment);
      return;
    }

    // Fallback to caller-provided behavior
    fallback();
  };

  const handleCPCSubmit = async () => {
    try {
      const applicationId = application?.id;
      let cpcFrontUrl: string | null = cpcFrontPreview;
      let cpcBackUrl: string | null = cpcBackPreview;

      if (cpcFrontFile) {
        const uploadRes = await uploadApplicationDocument(
          cpcFrontFile,
          applicationId,
          "cpc-front"
        );
        if (!uploadRes.success) {
          Sentry.captureException(uploadRes);
          toast({
            title: "Upload Error",
            description: uploadRes.message || "Failed to upload CPC front",
            variant: "destructive",
          });
          return;
        }
        cpcFrontUrl = uploadRes.url || null;
      }

      if (cpcBackFile) {
        const uploadRes = await uploadApplicationDocument(
          cpcBackFile,
          applicationId,
          "cpc-back"
        );
        if (!uploadRes.success) {
          Sentry.captureException(uploadRes);
          toast({
            title: "Upload Error",
            description: uploadRes.message || "Failed to upload CPC back",
            variant: "destructive",
          });
          return;
        }
        cpcBackUrl = uploadRes.url || null;
      }

      const result = await submitCPC.mutateAsync({
        applicationId: application.applicationId,
        cpcFrontUrl: cpcFrontUrl || "",
        cpcBackUrl: cpcBackUrl || "",
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit CPC certificate",
          variant: "destructive",
        });
        return;
      }

      setSubmittedTabs((prev) => [
        ...prev.filter((t) => t !== "cpc-upload"),
        "cpc-upload",
      ]);
      toast({
        title: "CPC Submitted",
        description: "Your CPC certificate has been submitted.",
      });
      await handleNextNavigation(result, () =>
        setActiveTab("tacho-card-upload")
      );
    } catch (error) {
      console.error("Error in CPC submission:", error);
      Sentry.captureException(error, {
        extra: {
          context: "useDocumentSubmissionHandlers.handleCPCSubmit",
          applicationId: application?.id,
        },
      });
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleTachoCardSubmit = async () => {
    try {
      const applicationId = application?.id;
      let tachoFrontUrl: string | null = null;
      let tachoBackUrl: string | null = null;

      // Reuse existing persisted URLs from previews if available
      if (tachoFrontPreview && /^https?:\/\//.test(tachoFrontPreview)) {
        tachoFrontUrl = tachoFrontPreview;
      }
      if (tachoBackPreview && /^https?:\/\//.test(tachoBackPreview)) {
        tachoBackUrl = tachoBackPreview;
      }

      if (tachoFrontFile) {
        const uploadRes = await uploadApplicationDocument(
          tachoFrontFile,
          applicationId,
          "tacho-front"
        );
        if (!uploadRes.success) {
          Sentry.captureException(uploadRes);
          toast({
            title: "Upload Error",
            description: uploadRes.message || "Failed to upload Tacho front",
            variant: "destructive",
          });
          return;
        }
        tachoFrontUrl = uploadRes.url || null;
      }

      if (tachoBackFile) {
        const uploadRes = await uploadApplicationDocument(
          tachoBackFile,
          applicationId,
          "tacho-back"
        );
        if (!uploadRes.success) {
          Sentry.captureException(uploadRes);
          toast({
            title: "Upload Error",
            description: uploadRes.message || "Failed to upload Tacho back",
            variant: "destructive",
          });
          return;
        }
        tachoBackUrl = uploadRes.url || null;
      }

      const result = await submitTachoCard.mutateAsync({
        applicationId: application.applicationId,
        tachoFrontUrl: tachoFrontUrl || "",
        tachoBackUrl: tachoBackUrl || "",
        tachoCardNumber: tachoCardNumber || "",
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit Tacho card",
          variant: "destructive",
        });
        return;
      }

      setSubmittedTabs((prev) => [
        ...prev.filter((t) => t !== "tacho-card-upload"),
        "tacho-card-upload",
      ]);
      toast({
        title: "Tacho Card Submitted",
        description: "Your Tacho card has been submitted.",
      });
      await handleNextNavigation(result, () =>
        setActiveTab("d4-medical-report-upload")
      );
    } catch (error) {
      console.error("Error in Tacho submission:", error);
      Sentry.captureException(error, {
        extra: {
          context: "useDocumentSubmissionHandlers.handleTachoCardSubmit",
          applicationId: application?.id,
        },
      });
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleD4MedicalSubmit = async () => {
    try {
      const applicationId = application?.id;
      let d4MedicalReportUrl: string | null = null;

      // Reuse existing persisted URL from preview if available
      if (d4MedicalPreview && /^https?:\/\//.test(d4MedicalPreview)) {
        d4MedicalReportUrl = d4MedicalPreview;
      }

      if (d4MedicalFile) {
        const uploadRes = await uploadApplicationDocument(
          d4MedicalFile,
          applicationId,
          "d4-medical"
        );
        if (!uploadRes.success) {
          Sentry.captureException(uploadRes);
          toast({
            title: "Upload Error",
            description:
              uploadRes.message || "Failed to upload D4 medical report",
            variant: "destructive",
          });
          return;
        }
        d4MedicalReportUrl = uploadRes.url || null;
      }

      const result = await submitD4Medical.mutateAsync({
        applicationId: application.applicationId,
        d4MedicalReportUrl: d4MedicalReportUrl || "",
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit D4 medical report",
          variant: "destructive",
        });
        return;
      }

      setSubmittedTabs((prev) => [
        ...prev.filter((t) => t !== "d4-medical-report-upload"),
        "d4-medical-report-upload",
      ]);
      toast({
        title: "D4 Medical Submitted",
        description: "Your D4 medical report has been submitted.",
      });
      await handleNextNavigation(result, () => {
        navigate("/orientation");
      });
    } catch (error) {
      console.error("Error in D4 Medical submission:", error);
      Sentry.captureException(error, {
        extra: {
          context: "useDocumentSubmissionHandlers.handleD4MedicalSubmit",
          applicationId: application?.id,
        },
      });
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleDVLASubmit = async () => {
    try {
      const applicationId = application?.id;

      // --- Client-side validation with toasts ---
      const hasFront =
        Boolean(licenseFrontFile) || Boolean(licenseFrontPreview);
      const hasBack = Boolean(licenseBackFile) || Boolean(licenseBackPreview);

      if (!drivingLicenceNumber || !hasFront || !hasBack) {
        toast({
          title: "Missing Information",
          description:
            "Please provide your driver license number AND attach both front and back photos (you can reuse previously uploaded images)",
          variant: "destructive",
        });
        return;
      }

      if (drivingLicenceNumber.length !== 16) {
        toast({
          title: "Invalid Driver License Number",
          description:
            "Driver license number must be exactly 16 characters long",
          variant: "destructive",
        });
        return;
      }

      if (!/^[a-zA-Z0-9]+$/.test(drivingLicenceNumber)) {
        toast({
          title: "Invalid Driver License Number",
          description:
            "Driver license number must contain only letters and numbers",
          variant: "destructive",
        });
        return;
      }

      // Start with persisted URLs if present (do not force re-upload)
      let licenseFrontUrl =
        licenseFrontPreview && /^https?:\/\//.test(licenseFrontPreview)
          ? licenseFrontPreview
          : null;
      let licenseBackUrl =
        licenseBackPreview && /^https?:\/\//.test(licenseBackPreview)
          ? licenseBackPreview
          : null;

      // Upload license front if available (overrides any existing URL)
      if (licenseFrontFile) {
        const frontResult = await uploadApplicationDocument(
          licenseFrontFile,
          applicationId,
          "dvla-front"
        );

        if (!frontResult.success) {
          Sentry.captureException(frontResult);
          toast({
            title: "Upload Error",
            description:
              frontResult.message || "Failed to upload license front",
            variant: "destructive",
          });
          return;
        }

        licenseFrontUrl = frontResult.url;
      }

      // Upload license back if available (overrides any existing URL)
      if (licenseBackFile) {
        const backResult = await uploadApplicationDocument(
          licenseBackFile,
          applicationId,
          "dvla-back"
        );

        if (!backResult.success) {
          Sentry.captureException(backResult);
          toast({
            title: "Upload Error",
            description: backResult.message || "Failed to upload license back",
            variant: "destructive",
          });
          return;
        }

        licenseBackUrl = backResult.url;
      }
      const newAttemptCount = dvlaAttempts + 1;
      setDvlaAttempts(newAttemptCount);
      // Submit to cloud function with attempt number
      const result = await submitDVLA.mutateAsync({
        applicationId: application.applicationId, // Use application ID from context
        drivingLicenceNumber,
        licenseFrontUrl,
        licenseBackUrl,
        attemptNumber: newAttemptCount, // Pass the current attempt number
        dvlaConsentAcceptedAt: new Date().toISOString(), // Timestamp of Data Protection Declaration acceptance
      });

      if (!result.success) {
        Sentry.captureException(result);
        // Check if it's a 404 error (license not found)
        if (result.message?.toLowerCase().includes("no driver record")) {
          // Calculate attempts remaining
          const attemptsRemaining = 3 - newAttemptCount;

          if (attemptsRemaining > 0) {
            toast({
              title: "License Not Found",
              description: `The driving license number you entered could not be verified. Please check the number and try again. You have ${attemptsRemaining} ${
                attemptsRemaining === 1 ? "attempt" : "attempts"
              } remaining.`,
              variant: "destructive",
            });
          } else {
            toast({
              title: "Maximum Attempts Reached",
              description:
                "You have reached the maximum number of attempts for license verification. Please contact support for assistance.",
              variant: "destructive",
            });
            await refetch();
            navigate("/hold");
          }

          // Don't navigate away, allow user to correct the license number
          return;
        } else {
          Sentry.captureException(result);
          // Handle other errors
          toast({
            title: "Submission Error",
            description: result.message || "Failed to submit DVLA information",
            variant: "destructive",
          });
          return;
        }
      }

      setSubmittedTabs((prev) => [
        ...prev.filter((t) => t !== "dvla-check"),
        "dvla-check",
      ]);
      toast({
        title: "DVLA Information Submitted",
        description:
          "Your DVLA documentation has been successfully submitted for verification.",
      });
      await handleNextNavigation(result, () => setActiveTab("rtw-check"));
    } catch (error) {
      console.error("Error in DVLA submission:", error);
      Sentry.captureException(error, {
        extra: {
          context: "useDocumentSubmissionHandlers.handleDVLASubmit",
          applicationId: application?.id,
          attemptNumber: dvlaAttempts + 1,
        },
      });
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleRTWSubmit = async () => {
    try {
      const applicationId = application?.id;

      let documentUrl: string | null = null;

      // Reuse existing persisted URL from preview if available
      if (documentPreview && /^https?:\/\//.test(documentPreview)) {
        documentUrl = documentPreview;
      }

      // Upload RTW document if a new file is available (overrides any existing URL)
      if (documentFile) {
        const documentResult = await uploadApplicationDocument(
          documentFile,
          applicationId,
          "rtw-document"
        );

        if (!documentResult.success) {
          Sentry.captureException(documentResult);
          toast({
            title: "Upload Error",
            description:
              documentResult.message || "Failed to upload RTW document",
            variant: "destructive",
          });
          return;
        }

        documentUrl = documentResult.url;
      }

      const newAttemptCount = rtwAttempts + 1;
      setRtwAttempts(newAttemptCount);

      // Submit to cloud function with attempt number
      const result = await submitRTW.mutateAsync({
        applicationId: application.applicationId, // Use application ID from context
        rtwShareCode,
        documentUrl,
        attemptNumber: newAttemptCount, // Pass the current attempt number
      });

      if (!result.success) {
        // Check if it's a verification failure
        if (
          result.message?.toLowerCase().includes("not found") ||
          result.message?.toLowerCase().includes("verification failed")
        ) {
          // Calculate attempts remaining
          const attemptsRemaining = 3 - newAttemptCount;

          if (attemptsRemaining > 0) {
            toast({
              title: "Share Code Verification Failed",
              description: `The RTW share code you entered could not be verified. Please check the code and try again. You have ${attemptsRemaining} ${
                attemptsRemaining === 1 ? "attempt" : "attempts"
              } remaining.`,
              variant: "destructive",
            });
          } else {
            toast({
              title: "Maximum Attempts Reached",
              description:
                "You have reached the maximum number of attempts for RTW verification. Please contact support for assistance.",
              variant: "destructive",
            });
            await refetch();
            navigate("/hold");
          }

          // Don't navigate away, allow user to correct the share code
          return;
        } else {
          Sentry.captureException(result);
          // Handle other errors
          toast({
            title: "Submission Error",
            description: result.message || "Failed to submit RTW information",
            variant: "destructive",
          });
          return;
        }
      }

      setSubmittedTabs((prev) => [
        ...prev.filter((t) => t !== "rtw-check"),
        "rtw-check",
      ]);
      toast({
        title: "Right to Work Information Submitted",
        description:
          "Your right to work documentation has been successfully submitted for verification.",
      });
      await handleNextNavigation(result, () => {
        navigate("/orientation");
      });
    } catch (error) {
      console.error("Error in RTW submission:", error);
      Sentry.captureException(error, {
        extra: {
          context: "useDocumentSubmissionHandlers.handleRTWSubmit",
          applicationId: application?.id,
          attemptNumber: rtwAttempts + 1,
        },
      });
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleInsuranceSubmit = async () => {
    try {
      const applicationId = application?.id;

      let insuranceProofUrl = null;

      // Upload insurance proof if available
      if (insuranceProofFile) {
        const insuranceResult = await uploadApplicationDocument(
          insuranceProofFile,
          applicationId,
          "insurance-proof"
        );

        if (!insuranceResult.success) {
          Sentry.captureException(insuranceResult);
          toast({
            title: "Upload Error",
            description:
              insuranceResult.message || "Failed to upload insurance proof",
            variant: "destructive",
          });
          return;
        }

        insuranceProofUrl = insuranceResult.url;
      }

      // Submit to cloud function
      const result = await submitInsurance.mutateAsync({
        applicationId: application.applicationId, // Use application ID from context
        insuranceProvider,
        policyNumber,
        insuranceProofUrl,
      });

      if (!result.success) {
        Sentry.captureException(result);
        toast({
          title: "Submission Error",
          description:
            result.message || "Failed to submit insurance information",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Insurance Information Submitted",
        description:
          "Your insurance documentation has been successfully submitted.",
      });
      await handleNextNavigation(result, () => {
        // Default navigation to orientation if no nextStage specified
        navigate("/orientation");
      });
    } catch (error) {
      console.error("Error in insurance submission:", error);
      Sentry.captureException(error, {
        extra: {
          context: "useDocumentSubmissionHandlers.handleInsuranceSubmit",
          applicationId: application?.id,
          insuranceProvider,
          policyNumberProvided: !!policyNumber,
        },
      });
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  return {
    handleDVLASubmit,
    handleRTWSubmit,
    handleInsuranceSubmit,
    handleCPCSubmit,
    handleTachoCardSubmit,
    handleD4MedicalSubmit,
  };
};
